import { useQueryClient } from '@tanstack/react-query';
import { type AssetListQueryParams, prefetchAssetList } from '@/hooks/data/assets/useAssetList';
import { getAlbumAssets, getFolderAssets } from '@/services/asset.service';
import { type ChildNodeType, DAMNodeType, toNodeType } from '@/types/tree';
import { useGallerySelection } from '@/context/GallerySelectionContext';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { useBrowserStore } from '@/hooks/data/browser/useBrowserStore';
import { useTableStore } from '@/hooks/data/datatable/useTableStore';
import { extractDateRangeClauses } from '@/utilities/search/filters';
import { useSearchStore } from '@/hooks/data/search/useSearchStore';
import { primeTreePathCache } from '@/hooks/data/tree/useTree';
import { useDataTablePagination } from '@/hooks/usePagination';
import { useTreeStore } from '@/hooks/data/tree/useTreeStore';
import { useCurrentPage } from '@/hooks/useCurrentPage';
import { useTreeContext } from '@/context/TreeContext';
import { SortOrderValues } from '@/types/sort';
import { EntityType } from '@/types/entity';
import { useRouter } from 'next/router';

export const useDamNavigation = () => {
  const { push, query: routerQuery, replace } = useRouter();
  // Selectors, not the whole store: many components mount this hook and a bare one re-renders them all.
  const setIsAllSelected = useBrowserStore((state) => state.setIsAllSelected);
  const setLocateAssetId = useBrowserStore((state) => state.setLocateAssetId);
  const { deselectAllItems } = useGallerySelection();
  const { setCurrentSelectedType, setSelectedNodes, includeSubFolderAssets } = useTreeContext();
  const { browseRedirection, pathSlugs, isBrowsePage } = useCurrentPage();
  const setPagination = useTableStore((state) => state.setPagination);
  const setSearchQuery = useSearchStore((state) => state.setSearchQuery);
  const queryClient = useQueryClient();
  const folderAssetsFn = useAuthenticatedQueryFn(getFolderAssets);
  const albumAssetsFn = useAuthenticatedQueryFn(getAlbumAssets);

  const setSelectedNode = useTreeStore((state) => state.setSelectedNode);
  const findNodePath = useTreeStore((state) => state.findNodePath);
  const findNodePathNodes = useTreeStore((state) => state.findNodePathNodes);
  // Same source the gallery's own asset fetch reads, so a prefetch can't drift from it.
  const { limit, sortOrder } = useDataTablePagination(EntityType.Asset);
  const assetQueryString = useTableStore((state) => state.queryStrings?.[EntityType.Asset] ?? '');

  // `selectedNode` clears after the route commits, not before: the gallery children band renders
  // off it alone, so clearing it earlier unmounts that band a render ahead of the route change.
  const navigateAndReset = async (pathname: string, query: any, entity = EntityType.Asset) => {
    setIsAllSelected(false);
    deselectAllItems();
    setSelectedNodes([]);
    setCurrentSelectedType(undefined);
    setLocateAssetId(undefined);
    setPagination(entity, { pageIndex: 0 });
    setSearchQuery('');
    // Only clear if it's still this value — a cached destination or a superseded call may set it first.
    const selectedNodeBeforePush = useTreeStore.getState().selectedNode;
    try {
      await push(
        {
          pathname,
          query,
        },
        undefined,
        { shallow: true },
      );
    } finally {
      if (useTreeStore.getState().selectedNode === selectedNodeBeforePush) {
        setSelectedNode(undefined, undefined);
      }
    }
  };

  // Replace the slug of a node in the URL if it exists in the current path (used after renaming a node)
  const replaceSlugInUrl = (nodeId?: string | null, nextSlug?: string | null) => {
    if (!nodeId || !nextSlug || !isBrowsePage || !pathSlugs || pathSlugs.length === 0) {
      return;
    }

    const currentPath = findNodePath(nodeId);

    if (!currentPath || currentPath.length === 0) {
      return;
    }

    const isNodeInCurrentPath = currentPath.every((segment, index) => segment === pathSlugs[index]);

    if (!isNodeInCurrentPath) {
      return;
    }

    const updatedPath = [...currentPath.slice(0, -1), nextSlug, ...pathSlugs.slice(currentPath.length)];

    if (updatedPath.join('/') === pathSlugs.join('/')) {
      return;
    }

    const { slug: _slugParam, ...restQuery } = routerQuery;

    void replace(
      {
        pathname: `/browse/${updatedPath.join('/')}`,
        query: restQuery,
      },
      undefined,
      { shallow: true },
    );
  };

  const navigateToSearch = (
    id: string,
    nodeType: DAMNodeType,
    fallbackSlugPath?: Array<string>,
    refine?: () => void,
  ) => {
    const separator = ' && ';
    const scopeFilter = `${nodeType === DAMNodeType.Folder ? 'parentId' : 'albumIds'}:"${id}"`;
    const preserved = extractDateRangeClauses(String(routerQuery.filters ?? ''));
    const newFilters = [scopeFilter, ...preserved].join(separator);
    const newQuery = {
      ...routerQuery,
      filters: newFilters,
      searchFilters: '',
    };
    setLocateAssetId(undefined);
    setPagination(EntityType.Asset, { pageIndex: 0, sortValue: SortOrderValues.CREATED_AT });

    const pathArray = findNodePath(id, 'slug') ?? fallbackSlugPath;
    const fullPath = '/browse/' + (pathArray ? pathArray.join('/') : '');

    void replace(
      {
        pathname: fullPath,
        query: newQuery,
      },
      undefined,
      { shallow: true },
    );
    if (refine) {
      refine();
    }
  };

  // navigateToNode resets pageIndex/sortValue right before calling this, so those are fixed
  // here rather than re-read — a stale render's pageIndex would prime the wrong offset.
  const prefetchNodeAssets = (id: string, type: ChildNodeType) => {
    const shared: AssetListQueryParams = {
      limit,
      offset: 0,
      sort: { order: sortOrder, value: SortOrderValues.CREATED_AT },
      queryString: assetQueryString,
      includeSubFolderAssets,
      includeChildren: true,
    };
    const fetchFns = { getFolderAssets: folderAssetsFn, getAlbumAssets: albumAssetsFn };

    if (type === DAMNodeType.Album) {
      prefetchAssetList(queryClient, { ...shared, albumId: id }, fetchFns);
      return;
    }

    prefetchAssetList(queryClient, { ...shared, folderId: id }, fetchFns);
  };

  /**
   * Navigate into a folder or album, clearing everything that was scoped to the node being left.
   *
   * Albums used to have no equivalent: callers reached for `browseRedirection` directly, which
   * moves the route but leaves `locateAssetId` pointing at an asset in the previous node and the
   * asset table's pageIndex/sortValue untouched. The node type is a parameter rather than a
   * hardcoded `DAMNodeType.Folder` precisely so there is nothing left to reimplement per caller.
   */
  const navigateToNode = async (id: string, name: string, type: ChildNodeType, path?: Array<string>) => {
    deselectAllItems();
    setSelectedNodes([{ id, name, type }]);
    setCurrentSelectedType(toNodeType(type));
    setIsAllSelected(false);
    setLocateAssetId(undefined);
    setPagination(EntityType.Asset, { pageIndex: 0, sortValue: SortOrderValues.CREATED_AT });
    const pathArray = path ?? findNodePath(id, 'slug');
    primeTreePathCache(queryClient, findNodePathNodes(id), pathArray);
    prefetchNodeAssets(id, type);
    await browseRedirection(pathArray);
  };

  /** Folder-only shorthand for the breadcrumb and sidebar-tree callers, which can only be folders. */
  const navigateToFolder = (id: string, name: string, path?: Array<string>) =>
    navigateToNode(id, name, DAMNodeType.Folder, path);

  return { navigateAndReset, navigateToNode, navigateToFolder, navigateToSearch, replaceSlugInUrl };
};
